import React, { ChangeEvent, useLayoutEffect, useRef } from "react";
import Close from "./SVGIcons/Close";
import { useAppContext } from "../hooks/useAppContext";
import { Transition } from "@headlessui/react";
import { getTranslation } from "../locales/getTranslation";
import Modal from "./ui/Modal";
import useWindowWidth from "../hooks/useWindowWidth";

type ModalFeedbackProps = {
  _id: string;
  feedbackOpen: boolean;
  setFeedbackOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onThumbDownClicked: (
    _id: string,
    feedbackChoices: string[],
    feedbackDetails: string,
  ) => Promise<void>;
};

const choices = [
  "Feedback #1",
  "Feedback #2",
  "Feedback #3",
  "Feedback #4",
  "Feedback #5",
];

const ModalFeedback = ({
  _id,
  feedbackOpen,
  setFeedbackOpen,
  onThumbDownClicked,
}: ModalFeedbackProps) => {
  const { locale, theme } = useAppContext();
  const [selectedFeedbackChoices, setSelectedFeedbackChoices] = React.useState<
    string[]
  >([]);
  const [error, setError] = React.useState("");
  const [feedbackDetails, setFeedbackDetails] = React.useState("");
  const textbox = useRef<HTMLTextAreaElement>(null);
  const width = useWindowWidth();

  const adjustHeight = () => {
    if (!textbox.current) return;

    textbox.current.style.height = "inherit";
    textbox.current.style.height = `${textbox.current.scrollHeight}px`;
  };

  const sendFeedback = () => {
    if (selectedFeedbackChoices.length === 0 && !feedbackDetails) {
      setError("Please select at least one feedback or provide details.");
      return;
    }

    setFeedbackOpen(false);
    onThumbDownClicked(_id, selectedFeedbackChoices, feedbackDetails);
  };

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      sendFeedback();
    }
  };

  const onFeedbackChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.currentTarget.value;
    setFeedbackDetails(value);
    setError("");
    adjustHeight();
  };

  useLayoutEffect(adjustHeight, [feedbackDetails]);

  return (
    <>
      <Modal open={feedbackOpen} setOpen={setFeedbackOpen}>
        <div
          className={`w-full rounded-[32px] bg-grayscale-solid-elevate-level1 p-8 shadow md:w-[600px] md:p-12`}
        >
          <div className="flex flex-col items-center justify-center gap-6">
            <h1 className="text-center text-xl font-bold leading-7 text-grayscale-solid-g10">
              {getTranslation(locale, "Provide additional feedback")}
            </h1>
            <div className="h-px w-full bg-grayscale-solid-g10" />
            <ul className="flex flex-wrap items-start justify-start gap-2">
              {choices.map((choice, index) => (
                <li
                  key={index}
                  className={`flex cursor-pointer items-center justify-start rounded-[30px] p-2.5 text-xs font-medium leading-[17px] text-grayscale-solid-g10 outline outline-1 outline-offset-[-1px] outline-grayscale-opacity-blevel3 ${selectedFeedbackChoices.includes(getTranslation(locale, choice) as string) ? "bg-grayscale-solid-g2" : "hover:bg-grayscale-solid-g2"}`}
                  onClick={() => {
                    setError("");
                    setSelectedFeedbackChoices((prev) => {
                      let transChoice = getTranslation(
                        locale,
                        choice,
                      ) as string;
                      if (prev.includes(transChoice)) {
                        return prev.filter((item) => item !== transChoice);
                      } else {
                        return [...prev, transChoice];
                      }
                    });
                  }}
                >
                  {getTranslation(locale, choice)}
                </li>
              ))}
            </ul>
            <div
              className={`group flex w-full items-center gap-4 rounded-[20px] px-[18px] py-2 shadow ring-1 ring-grayscale-solid-g2 hover:ring-grayscale-solid-g6 ${theme === "dark" ? "bg-grayscale-solid-g2" : "bg-grayscale-solid-g0"}`}
            >
              <textarea
                className={`max-h-[75px] w-full resize-none break-words pr-1 text-sm leading-snug text-grayscale-solid-g10 ${theme === "dark" ? "bg-grayscale-solid-g2" : "bg-grayscale-solid-g0"} focus:outline-none focus:ring-0`}
                placeholder={getTranslation(locale, "Feedback Placeholder")}
                value={feedbackDetails}
                onChange={onFeedbackChange}
                onKeyDown={onEnterPress}
                aria-label="Feedback"
                rows={width <= 700 ? 2 : 1}
                ref={textbox}
              />
            </div>
            <div className="flex w-full items-center justify-start gap-4">
              <button
                className="flex h-[30px] w-[100px] items-center justify-center rounded-[14px] bg-solid-level6 px-6 py-3 text-sm font-bold leading-snug text-white hover:bg-solid-level5"
                onClick={sendFeedback}
              >
                {getTranslation(locale, "Submit")}
              </button>
              <div className="flex h-[12px] items-center text-sm font-bold text-red-solid-red6">
                {error}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ModalFeedback;
